import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserSchema } from '../../../definitions/schemas';
import AuthService, { AuthError } from '../../../Services/AuthService';
import AuthTokenProviderInstance from '../../../Services/Auth/AuthTokenProvider';
import ChromeExtensionService from '../../../Services/ChromeExtension/ChromeExtensionService';
import UserStore, {
  UserPendingActionsStore,
} from '../../../stores/observables/UserStore';

interface SignInState {
  isLoggedIn: boolean;
  isNewUser: boolean;
  isReady: boolean;
  signInError: string | undefined;
}

const useSignIn = (readyToSignIn: boolean): SignInState => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [signInError, setSignInError] = useState<string>();

  const state = useMemo<SignInState>(
    () => ({ isLoggedIn, isNewUser, isReady, signInError }),
    [isLoggedIn, isNewUser, isReady, signInError]
  );

  useEffect(() => {
    /**
     * Waits for the flag for actually get the signed in user information to
     * avoid any race conditions that could happen if the session was obtained
     * from an OTT exchange
     */
    if (readyToSignIn) {
      getSignedInUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToSignIn]);

  const setSignedInUser = useCallback((user: UserSchema) => {
    UserStore.setExtended(user);

    const isNewUser = !user.userRoles || user.userRoles.length === 0;
    setIsNewUser(isNewUser);
    setIsLoggedIn(true);
    setIsReady(true);

    /**
     * At this point, since the user has been authenticated successfully,
     * we use the chrome extension messaging API to notify the authentication
     * action with the auth token. It's up to the chrome extension to act
     * accordingly.
     */
    new ChromeExtensionService().notifyAuthentication(
      AuthTokenProviderInstance.getAuthenticationToken()
    );
  }, []);

  const getSignedInUser = useCallback(() => {
    AuthService.getUser(
      async (user: UserSchema) => {
        setIsLoggedIn(true);
        UserPendingActionsStore.set({
          changePassword: !!user.pendingPasswordChange,
        });
        setSignedInUser(user);
      },
      (error) => {
        // catching this particular error if the user is not authenticated
        // to redirect the user to the login page
        if (error === AuthError.NOT_LOGGED_IN) {
          setIsLoggedIn(false);
          setIsReady(true);
          return;
        }

        // but if there's another error and the user is logged in, we turn on
        // the flags to display a modal with the error message
        setSignInError(error);
      },
      'subscribeAuthService'
    );
  }, [setSignedInUser]);

  return state;
};

export default useSignIn;
